import type { Store } from '../state';

export interface TemplateStackI18n {
  t(key: string, params?: Record<string, string | number>): string;
  onLocaleChange(fn: () => void): void;
}

export interface TemplateStackCallbacks {
  onExport(): void;
  onImportFile(file: File): void;
  onRebind(templateRegionId: string, regionUuid: string | null): void;
}

export function createTemplateStackPanel(opts: {
  parent: HTMLElement;
  i18n: TemplateStackI18n;
  store: Store;
  callbacks: TemplateStackCallbacks;
}): HTMLElement {
  const { parent, i18n, store, callbacks } = opts;
  const panel = document.createElement('section');
  panel.className = 'template-stack-panel';

  const actions = document.createElement('div');
  actions.className = 'region-list-toolbar';
  actions.setAttribute('role', 'toolbar');
  actions.setAttribute('aria-label', i18n.t('template.toolbar'));

  const exportButton = document.createElement('button');
  exportButton.type = 'button';
  exportButton.className = 'region-list-toolbar-btn';
  exportButton.dataset['i18n'] = 'template.export';
  exportButton.textContent = i18n.t('template.export');
  exportButton.addEventListener('click', callbacks.onExport);

  const importButton = document.createElement('button');
  importButton.type = 'button';
  importButton.className = 'region-list-toolbar-btn';
  importButton.dataset['i18n'] = 'template.import';
  importButton.textContent = i18n.t('template.import');

  const importLabel = document.createElement('label');
  importLabel.className = 'sr-only';
  importLabel.htmlFor = 'ctrl-templateImport';
  importLabel.textContent = i18n.t('template.import');

  const importInput = document.createElement('input');
  importInput.type = 'file';
  importInput.id = 'ctrl-templateImport';
  importInput.accept = '.amtt,application/zip,application/json';
  importInput.className = 'sr-only';
  importInput.setAttribute('aria-label', i18n.t('template.import'));
  importInput.addEventListener('change', () => {
    const file = importInput.files?.[0];
    if (file) callbacks.onImportFile(file);
    importInput.value = '';
  });
  importButton.addEventListener('click', () => importInput.click());

  actions.appendChild(exportButton);
  actions.appendChild(importButton);
  actions.appendChild(importLabel);
  actions.appendChild(importInput);
  panel.appendChild(actions);

  const title = document.createElement('h3');
  title.className = 'template-stack-title';
  panel.appendChild(title);

  const list = document.createElement('div');
  list.className = 'template-stack-list';
  panel.appendChild(list);

  function sync(): void {
    const state = store.get();
    const template = state.templateStack;
    title.textContent =
      template.name ?? i18n.t('template.empty');
    exportButton.disabled = state.regionEntries.length === 0;
    list.replaceChildren();

    if (template.bindings.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'template-stack-empty';
      empty.textContent = i18n.t('template.noBindings');
      list.appendChild(empty);
      return;
    }

    for (const binding of template.bindings) {
      const row = document.createElement('div');
      row.className = 'template-binding-row';

      const swatch = document.createElement('span');
      swatch.className = 'template-binding-swatch';
      swatch.style.backgroundColor = `rgb(${binding.rgb.join(',')})`;
      swatch.setAttribute('aria-hidden', 'true');
      row.appendChild(swatch);

      const selectId = `ctrl-template-bind-${binding.templateRegionId}`;
      const label = document.createElement('label');
      label.htmlFor = selectId;
      label.textContent = i18n.t('template.bindingLabel', {
        rgb: binding.rgb.join(', '),
      });
      row.appendChild(label);

      const select = document.createElement('select');
      select.id = selectId;
      const none = document.createElement('option');
      none.value = '';
      none.textContent = i18n.t('template.unbound');
      select.appendChild(none);
      for (const region of state.regionEntries) {
        const option = document.createElement('option');
        option.value = region.uuid;
        option.textContent = `${region.userName} (${region.rgb.join(', ')})`;
        select.appendChild(option);
      }
      select.value = binding.boundRegionUuid ?? '';
      select.addEventListener('change', () => {
        callbacks.onRebind(binding.templateRegionId, select.value || null);
      });
      row.appendChild(select);
      const candidateRegions = (binding.candidateRegionUuids ?? [])
        .map((uuid) => state.regionEntries.find((region) => region.uuid === uuid))
        .filter((region): region is NonNullable<typeof region> => region !== undefined);
      if (candidateRegions.length > 0) {
        const candidateText = document.createElement('p');
        candidateText.className = 'template-binding-candidates';
        candidateText.textContent = i18n.t('template.candidateSummary', {
          regions: candidateRegions
            .map((region) => `${region.userName} (${region.rgb.join(', ')})`)
            .join(', '),
        });
        row.appendChild(candidateText);
      }
      list.appendChild(row);
    }
  }

  i18n.onLocaleChange(sync);
  store.subscribe((_state, patch) => {
    if (
      'templateStack' in patch ||
      'regionEntries' in patch ||
      'uiActiveTab' in patch
    ) {
      sync();
    }
  });

  sync();
  parent.appendChild(panel);
  return panel;
}
